/* ============================================================
   SLATE AI Help Assistant — Knowledge Base
   Roles: 1=Admin, 2=Teacher, 3=StateOfficer
============================================================ */

export const KNOWLEDGE = {
  'add school': {
    title: 'How to Add a School',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to School section in the left sidebar.',
      'Click "Add School" from the dropdown menu.',
      'Fill School Name (max 150 chars) and UDISE Code (must be unique).',
      'Select State and District from the dropdowns.',
      'Enter Pincode and Address.',
      'Optionally fill: Geo Location, School Email ID, Primary Contact Person, Contact No, Total Students.',
      'Click Submit to save the school.',
    ],
    note: 'UDISE code must be unique. All three roles (Admin, Teacher, State Officer) can add schools.',
  },

  'tag lab to school': {
    title: 'How to Tag a Lab to a School',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to School in the sidebar.',
      'Click "Tag Lab to School".',
      'Enter Lab Name (required).',
      'Select Lab Type: Atal Tinkering Lab, Aghaaz Lab, SATHEE, or Artificial Intelligence Lab.',
      'Enter Lab Capacity (number of students) and Lab Description (max 500 chars).',
      'Select the School Name from the dropdown.',
      'Click Submit to link the lab to the school.',
    ],
    note: 'A lab must be tagged to a school before sessions can be created. All three roles can tag labs.',
  },

  'add equipment': {
    title: 'How to Add Equipment',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to School in the sidebar.',
      'Click "Add Equipment".',
      'Enter Equipment Name and Equipment Description.',
      'Select Warranty Status from the dropdown.',
      'Enter Quantity, Date of Expiry, and Serial Number.',
      'Click Submit to save the equipment.',
    ],
    note: 'After adding equipment, use Equipment Allocation to assign it to a school or lab.',
  },

  'equipment allocation': {
    title: 'How to Allocate Equipment',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to School in the sidebar.',
      'Click "Equipment Allocation".',
      'Select one or more School Names from the multi-select list.',
      'Select the Lab Name from the dropdown.',
      'Select one or more Equipment Names from the multi-select list (shows available quantity).',
      'Set the Allocation Date.',
      'The Available Quantity field auto-fills. Enter the Allocation Quantity.',
      'Click Submit to complete the allocation.',
    ],
    note: 'Equipment must be added first before it can be allocated. All three roles can allocate equipment.',
  },

  'session': {
    title: 'How to Create a Session',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to School in the sidebar.',
      'Click "Session Setup".',
      '— Section 1: Enter Session Title, select Session Host, Session Date, Session Time (with AM/PM).',
      'Select School Name and Lab Name from dropdowns.',
      'Enter Session Description (max 300 chars).',
      '— Section 2 (SATHEE sessions): Fill Centre Code, State, District/Block/Zone.',
      'Enter Name of Coordinator, select Type of School Visited.',
      'Enter School Address, Principal Name, and Principal/POC Contact.',
      'Select Mode of Visit: In-person, Virtual, or Phone Call.',
      'Upload List of Invitees Excel file (.xls/.xlsx) — Download Template link is available.',
      'Upload Session Images (optional, max 10 images — appear in PDF reports).',
      'Click Submit at the bottom.',
    ],
    note: 'The Invitees Excel file is required. Lab must be tagged to a school before a session can be created.',
  },

  'my entries': {
    title: 'How to Use My Entries — All Entries',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to School in the sidebar.',
      'Click "My Entries - All Entries".',
      '— TOGGLE: "My Entities" shows only your own data. "All Entities" shows all data (Admin sees all; Teacher and State Officer see their own).',
      '',
      '📂 SESSIONS TAB',
      '  • Table shows: Session Title, Session Host, Session Date, Setup By, Images, Download, Action (Track).',
      '  • The list strictly sorts chronologically from newest session date to oldest session date!',
      '  • Download button: downloads ALL session images as a ZIP file.',
      '  • Admin: can download images for ALL sessions (All Entities toggle).',
      '  • Teacher: can only download images for their OWN sessions (My Entities view only).',
      '  • State Officer: CANNOT download session images — no Download button visible.',
      '  • Track button: opens session tracking/attendance details.',
      '',
      '📂 SATHEE STUDENTS TAB',
      '  • Table shows: ID, Student Name, School, Mobile Number, Email, Registration Date, Action (Delete).',
      '  • Admin sees all registered students. State Officers see their state\'s students. Teachers see their lab\'s students.',
    ],
    note: 'Session images download as a ZIP file named "SessionTitle_images.zip". State Officers cannot download session images.',
  },

  
  'student registration': {
    title: 'How to Register a Student',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to Students → Student Registration in the sidebar.',
      'Select your Lab from the dropdown (SATHEE labs are excluded here).',
      'Fill in Student Name, Mobile Number, Email ID, Date of Birth, Gender, Grade, and Category.',
      'Provide Address, State, District, and Pincode.',
      'Click the Register Student button.',
    ],
    note: 'This form is for general students. All registered students are stored in the unified database under their respective lab.',
  },

  'sathee student registration': {
    title: 'How to Register a SATHEE Student',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to Students → Sathee Student Registration in the sidebar.',
      'Select your SATHEE Lab from the dropdown.',
      'Fill in the student details (similar to general registration).',
      'Click Register Student to securely add them to the SATHEE database.',
    ],
    note: 'These students are specifically tagged with SATHEE lab IDs, ensuring their data remains strictly isolated for reporting.',
  },

  'student assessments': {
    title: 'How to Add Student Assessments',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to Students → Student Assessments.',
      'Select a Lab to automatically fetch all students registered under that lab.',
      'Use the searchable dropdown to quickly find and select a specific student.',
      'Enter the Subject, Assessment Name, Score Achieved, and Max Score.',
      'Optionally add Feedback or Remarks.',
      'Click Save Assessment.',
    ],
    note: 'You can instantly search through thousands of students using the new searchable dropdown feature.',
  },

  'view students': {
    title: 'How to View Students',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to Students → View Students in the sidebar.',
      'A table will display all students you have access to see.',
      'Use the "Show [10] entries" dropdown or the Search box to filter records.',
      'You can export the filtered list to CSV or Excel, or Print the table.',
    ],
    note: 'Teachers only see students registered to their assigned lab. District Officers see students in their district. Admin sees all students.',
  },

  'student reports': {
    title: 'Student Advanced Reports & Exports',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to Students -> Reports in the sidebar.',
      'Select Lab (if applicable).',
      'Select a Report Type: Student Registration, Assessment Details, Combined Students & Assessments, or Kendra Details.',
      'Apply filters like State, District, Class, Exam, or Dates.',
      'Click Download Excel to get the tailored report.',
    ],
    note: 'The Kendra Details report will intelligently download a list of Kendras instead of students.',
  },

  'library': {
    title: 'How to Use the Library',
    roles: [1, 2, 3, 4],
    steps: [
      'Click "Library" in the left sidebar.',
      '— Search: Search artifacts by typing keywords or selecting tags.',
      '— My Artifacts: Shows only documents you have uploaded.',
      '— Upload Document: Click Upload Document, select your file, choose Document Type, add Tags, write a Description, toggle Publish, and click Submit.',
      '— Add URL: Click Add URL, enter Title and URL, add Tags, select Document Type, write Description, toggle Publish, and click Submit.',
      '— Manage All Artifacts (Admin only): View and edit/delete any document uploaded by any user.',
    ],
    note: 'File size must be within the upload limit set in System Settings (currently 2500 KB).',
  },

  'reports': {
    title: 'Reports & Analytics — Overview',
    roles: [1, 2, 3, 4],
    steps: [
      'Click "Reports" in the sidebar.',
      'The page has 3 tabs: Dashboard Stats, Download Reports (Excel), and PDF Reports.',
      '— Admin: Full Access · 26 reports available · can filter by State.',
      '— State Officer: Your State Only · 22 reports · state filter is locked to your state.',
      '— Teacher: Your Lab Only · 11 reports · marked "Restricted to your assigned lab".',
    ],
    note: 'Admin sees all states and labs. State Officer sees only their state data. Teacher sees only their assigned lab data.',
  },

  'dashboard stats': {
    title: 'Dashboard Stats — Reports',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to Reports → Dashboard Stats tab.',
      'Summary cards show: Total Sessions, Total Attendees, Active Labs, Active Teachers, Schools.',
      'A Sessions per Month bar chart is shown below.',
      'Tables below the chart: Sessions by Lab, Sessions by Teacher, Sessions by School.',
      '— Admin: Filter by From Date, To Date, and State. Click Apply Filter.',
      '— State Officer: Filter by date only (state is auto-applied).',
      '— Teacher: Filter by date only (lab is auto-applied).',
    ],
    note: 'Admin sees platform-wide data. State Officer and Teacher see scoped data only.',
  },

  'excel reports': {
    title: 'Download Reports — All Excel Report Types',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to Reports → Download Reports tab.',
      'Select a Report Type from the dropdown.',
      'Select From Date and To Date.',
      'Click Download Excel Report.',
    ],
    note: 'Data is automatically scoped based on your role (Admin, State Officer, or Teacher).',
  },

  'pdf reports': {
    title: 'Global PDF Reports',
    roles: [1, 2, 3, 4],
    steps: [
      'Go to Reports → PDF Reports tab.',
      'Select a Report Type (e.g., Session Detail Report).',
      'Select Session Title.',
      'Click Download PDF.',
    ],
    note: 'The PDF will contain all session details and up to 10 embedded session images.',
  },

  'grievance': {
    title: 'How to Submit a Grievance',
    roles: [1, 2, 3, 4],
    steps: [
      'Click "Grievance" in the sidebar.',
      'Click the "Submit Grievance" tab.',
      'Select an Issue Category (e.g. Hardware, Software, Platform).',
      'Select a Sub-Category.',
      'Enter the Description of the issue (max 500 chars).',
      '— Upload Image: Attach a PNG or JPG image (max 5MB) if available.',
      'Click Submit Grievance button.',
    ],
    note: 'After submission, a unique Grievance ID is assigned. Track its status under the My Grievances tab.',
  },

  'my grievances': {
    title: 'How to View My Grievances',
    roles: [1, 2, 3, 4],
    steps: [
      'Click "Grievance" in the sidebar.',
      'Click the "My Grievances" tab.',
      'You will see all grievances you submitted.',
      'Each row shows: Grievance ID, Status, Category, and Description.',
      'Status flow: Pending → In Progress → Resolved → Closed.',
    ],
    note: 'Admins can view and manage ALL grievances via God Mode → Manage Grievances.',
  },

  'profile': {
    title: 'How to Update My Profile',
    roles: [1, 2, 3, 4],
    steps: [
      'Click "My Profile" in the sidebar.',
      'View your avatar (with initials), Name, Email, Role badge, Scope, and Phone.',
      '— Admin scope shows "All India".',
      'To change password: scroll to Reset Password section.',
      'Enter Old Password, New Password, and Confirm Password.',
      'Click Update Password button.',
    ],
    note: 'Email cannot be changed from the profile page. Contact your Administrator if your email needs to be updated.',
  },

  'god mode': {
    title: 'God Mode — Admin-Only Tools',
    roles: [1],
    steps: [
      'God Mode is visible only to Administrators (shown in yellow in the sidebar).',
      '⚙️ System Settings — Set file upload limit and toggle allowed document formats.',
      '👁️ Check User Activity — Track user activity logs.',
      '👤 User Access — Create, edit, and manage all users and roles.',
      '🔒 RBAC — Toggle sidebar feature access per user.',
      '🧪 Add Lab Type — Add/Edit/Delete lab types.',
      '📄 Define Document Type — Add new document categories for the Library.',
      '🗂️ Manage All Artifacts — View all uploaded documents.',
      '🏷️ Edit Tags — View and edit tags.',
      '🏫 Manage Schools & Labs — View school cards with UDISE, location, contact.',
      '📢 Manage Grievances — Full grievance management with status updates.',
    ],
    note: 'God Mode is ONLY for Administrators. Teachers and State Officers have no access to any God Mode feature.',
  },

  'manage grievances': {
    title: 'How to Manage Grievances (Admin — God Mode)',
    roles: [1],
    steps: [
      'Go to God Mode → Manage Grievances in the sidebar.',
      'Filter by Status or Priority. Search by ID, user email, or category.',
      'Click any grievance row to open the Grievance Details modal.',
      'Select new Status from dropdown: Pending / In Progress / Resolved / Closed.',
      'Add Remarks / Resolution Notes in the textarea.',
      'Click the green Update Status button to save changes.',
    ],
    note: 'Only Administrators can manage all grievances. Priority levels are: Low, Medium, High, Critical.',
  },

  'add user': {
    title: 'How to Add a New User & Data Isolation',
    roles: [1],
    steps: [
      'Go to God Mode -> User Access.',
      'Click the Create User button.',
      'Fill: First Name, Last Name, Email, Phone.',
      'Select User Type: Admin, Teacher, State Officer, or District Officer.',
      'Select the geographic scope (State/District).',
      'Select Lab Type to Assign (MANDATORY for Teachers).',
      'For Teachers, School is optional. If left blank, their first created school will auto-assign to them.',
      'Click Submit to create the account.',
    ],
    note: 'Teachers and Officers are automatically restricted to only see data from their assigned lab and geographic scope.',
  },

  'data visibility': {
    title: 'Data Visibility & Scoping Rules',
    roles: [1, 2, 3, 4],
    steps: [
      'Teachers: Strictly isolated. Can only see Schools, Labs, Students, Kendras, and Assessments mapped to their assigned School/Lab, or entries they created themselves.',
      'District Officers: Restricted to all Schools, Labs, Students, Kendras, and Assessments within their assigned District.',
      'State Officers: Restricted to their assigned State and assigned Lab.',
      'Students Section: The entire Students section (Dashboard, Kendras, Registrations, Assessments, Reports) is strictly Role-Based. Teachers only see their own students.',
      'God Mode: Restricted to Admins only. The backend actively blocks any unauthorized API requests.'
    ],
    note: 'These visibility rules are enforced securely at the database level by the role middleware and tenancy utilities.',
  },

  'rbac': {
    title: 'RBAC — Role Based Access Control',
    roles: [1],
    steps: [
      'Go to God Mode → RBAC.',
      'The table shows all users with their Username, Title (role shown in color).',
      'Toggle columns control access to: Add School, Tag Lab to School, Add Equipment, Equipment Allocation, Session Setup, and Reports.',
      'Toggle ON to grant access, toggle OFF to revoke.',
    ],
    note: 'RBAC gives fine-grained control — e.g. you can give a Teacher access to Reports but block Equipment Allocation.',
  },

  'system settings': {
    title: 'System Settings (God Mode)',
    roles: [1],
    steps: [
      'Go to God Mode → System Settings.',
      'Update File Upload Limit or Total Space Allocated.',
      'Toggle allowed Document Formats For Library (jpg, png, pdf, docx, etc).',
    ],
    note: 'Changes to upload limit and allowed formats affect all users immediately.',
  },

  'check user activity': {
    title: 'Check User Activity (God Mode)',
    roles: [1],
    steps: [
      'Go to God Mode → Check User Activity.',
      'Select a user from the Users dropdown.',
      'Select a Period: Last 30 days, Last 90 days, Last 180 days, or Last 365 days.',
      'Click the Track button to see the logs.',
    ],
    note: 'Useful for auditing user actions and monitoring platform engagement.',
  },

  'getting started': {
    title: 'Getting Started with SLATE',
    roles: [1, 2, 3, 4],
    steps: [
      'Step 1 — Add Schools: School → Add School (all roles).',
      'Step 2 — Tag Labs to Schools: School → Tag Lab to School (all roles).',
      'Step 3 — Add Equipment: School → Add Equipment (all roles).',
      'Step 4 — Allocate Equipment to Labs: School → Equipment Allocation (all roles).',
      'Step 5 — Create Sessions: School → Session Setup (all roles).',
      'Step 6 — Register Students: Students → Student Registration (or Sathee Student Registration).',
      'Step 7 — Perform Assessments: Students → Student Assessments.',
      'Step 7 — Submit Grievances if issues arise: Grievance in sidebar (all roles).',
      'Step 8 — Generate Reports: Reports in sidebar (scoped by role).',
      'Step 9 — Admin only: Manage users, RBAC, and system settings via God Mode.',
    ],
    note: 'Follow this order for correct setup. Labs must exist before Sessions. Equipment must be added before Allocation.',
  },
};

export const KEYWORD_MAP = [
  { keys: ['add school', 'create school', 'new school', 'school add', 'school banana', 'school kaise'], key: 'add school' },
  { keys: ['tag lab', 'lab tag', 'attach lab', 'link lab', 'add lab', 'lab school'], key: 'tag lab to school' },
  { keys: ['add equipment', 'new equipment', 'equipment add', 'equipment banana'], key: 'add equipment' },
  { keys: ['allocate equipment', 'equipment allocation', 'assign equipment', 'equipment assign'], key: 'equipment allocation' },
  { keys: ['session setup', 'create session', 'add session', 'new session', 'session create', 'session kaise', 'setup session', 'session banana'], key: 'session' },
  
  { keys: ['my entries', 'all entries', 'view session', 'see session', 'session list', 'session dekho', 'download image', 'session image', 'zip download', 'image download', 'sathee students', 'all schools', 'all labs', 'allocated equipment'], key: 'my entries' },
  { keys: ['student registration', 'register student', 'add student'], key: 'student registration' },
  { keys: ['sathee registration', 'register sathee', 'sathee'], key: 'sathee student registration' },
  { keys: ['student assessments', 'add marks', 'assessments', 'student score'], key: 'student assessments' },
  { keys: ['view students', 'student list', 'all students'], key: 'view students' },
  { keys: ['student reports', 'export students'], key: 'student reports' },
  { keys: ['submit grievance', 'file grievance', 'add grievance', 'complaint', 'report issue', 'raise grievance', 'new grievance', 'shikayat'], key: 'grievance' },
  { keys: ['my grievance', 'view grievance', 'grievance status', 'check grievance', 'grievance list'], key: 'my grievances' },
  { keys: ['manage grievance', 'grievance manage', 'all grievance', 'admin grievance', 'god mode grievance'], key: 'manage grievances' },
  { keys: ['library', 'upload document', 'document upload', 'add url', 'artifact', 'upload file'], key: 'library' },
  { keys: ['pdf report', 'pdf download', 'download pdf'], key: 'pdf reports' },
  { keys: ['excel report', 'download report', 'excel', 'download excel'], key: 'excel reports' },
  { keys: ['dashboard stat', 'report stat', 'analytics', 'report chart', 'sessions chart'], key: 'dashboard stats' },
  { keys: ['report', 'generate report', 'reports kaise'], key: 'reports' },
  { keys: ['god mode', 'admin tools', 'god mod'], key: 'god mode' },
  { keys: ['add user', 'create user', 'new user', 'invite user', 'register user'], key: 'add user' },
  { keys: ['data visibility', 'scoping rules', 'who can see what', 'permissions', 'isolation', 'dashboard scope'], key: 'data visibility' },
  { keys: ['rbac', 'role based', 'feature access', 'toggle access', 'access control'], key: 'rbac' },
  { keys: ['system settings', 'upload limit', 'storage', 'file limit', 'document format'], key: 'system settings' },
  { keys: ['user activity', 'check activity', 'activity log', 'track user'], key: 'check user activity' },
  { keys: ['start', 'getting started', 'how to use', 'help me', 'guide', 'workflow', 'kya karu', 'process'], key: 'getting started' }
];
